import { useReassignVisitPermissionsFilter } from '@/components/visits/dialogs/ReassignVisits/reassignVisitPermissions';
import { VisitOutcome, VisitPermissions, VisitState } from '@/types/graphql';
import { Objects } from '@/types/objects';
import { isOutcomeReschedulable, isVisitComplete } from '@/types/visit';
import { useConfigValue } from '@/utils/useConfigValue';

type VisitCancelPermission = {
    state: VisitState;
    permissions: Objects.SubType<VisitPermissions, 'cancel'>;
};
type UseCanCancelVisit = <T extends VisitCancelPermission>(visit?: T | null) => boolean;

export const useCanCancelVisit: UseCanCancelVisit = (visit) => {
    const visitEditCancelUserPermission = useConfigValue('visit.edit.cancel');
    return (
        !!visit &&
        visitEditCancelUserPermission &&
        !isVisitComplete(visit.state) &&
        visit.permissions.cancel
    );
};

/**
 * User-level permission check for visit cancellation.
 * Returns true if the current user has the `visit.edit.cancel` config permission,
 * regardless of any specific visit's state or per-visit permissions.
 */
export const useHasVisitCancelPermission = (): boolean => {
    return useConfigValue('visit.edit.cancel');
};

type VisitReschedulePermission = {
    state: VisitState;
    permissions: Objects.SubType<VisitPermissions, 'reschedule' | 'assign'>;
};
type UseCanRescheduleVisit = <T extends VisitReschedulePermission>(visit?: T | null) => boolean;

export const useCanRescheduleVisit: UseCanRescheduleVisit = (visit) => {
    const hasBookRescheduleButton = useConfigValue('visit.book_reschedule_button');
    return (
        !!visit &&
        hasBookRescheduleButton &&
        !isVisitComplete(visit.state) &&
        visit.permissions.reschedule &&
        visit.permissions.assign
    );
};

type VisitReassignPermission = {
    state: VisitState;
    outcome: VisitOutcome;
    permissions: Objects.SubType<
        VisitPermissions,
        'assign' | 'editState' | 'editOutcome' | 'reschedule'
    >;
};
type UseCanReassignVisit = <T extends VisitReassignPermission>(visit?: T | null) => boolean;

export const useCanReassignVisit: UseCanReassignVisit = (visit) => {
    const reassignVisitFilter = useReassignVisitPermissionsFilter();
    return (
        !!visit &&
        (!isVisitComplete(visit.state) || isOutcomeReschedulable(visit.outcome)) &&
        (reassignVisitFilter
            ? reassignVisitFilter(visit.permissions)
            : visit.permissions.reschedule && visit.permissions.assign)
    );
};

// Helper to check if a visit has services for reassignment
type VisitWithServices = {
    clinicianAppointments:
        | {
              services: { id: string }[];
          }[]
        | null;
};
export const visitHasServices = <T extends VisitWithServices>(visit?: T | null): boolean => {
    if (!visit) {
        return false;
    }
    return (
        visit.clinicianAppointments?.some((appointment) => appointment.services.length > 0) ?? false
    );
};

export const VISIT_NO_SERVICES_TOOLTIP = 'Visit must have a service' as const;
